import { hash3i, toUnit } from './hash';

/**
 * Coordinate-and-seed pure noise field.
 *
 * All functions here are stateless: `simplex3(x, y, z, seed)` returns the same
 * value on the main thread, in any worker, and in any session. Terrain
 * generation depends on that property for crack-free chunk borders.
 */

const F3 = 1 / 3;
const G3 = 1 / 6;

// The 12 edge-midpoint gradients of a cube, the standard simplex gradient set.
const GRAD3 = new Float64Array([
  1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1, 0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1,
  -1,
]);

function gradDot(hash: number, x: number, y: number, z: number): number {
  const i = (hash % 12) * 3;
  return GRAD3[i] * x + GRAD3[i + 1] * y + GRAD3[i + 2] * z;
}

/** 3D simplex noise in roughly [-1, 1]. */
export function simplex3(xin: number, yin: number, zin: number, seed = 0): number {
  const s = (xin + yin + zin) * F3;
  const i = Math.floor(xin + s);
  const j = Math.floor(yin + s);
  const k = Math.floor(zin + s);
  const t = (i + j + k) * G3;
  const x0 = xin - (i - t);
  const y0 = yin - (j - t);
  const z0 = zin - (k - t);

  let i1: number, j1: number, k1: number;
  let i2: number, j2: number, k2: number;
  if (x0 >= y0) {
    if (y0 >= z0) {
      i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
    } else if (x0 >= z0) {
      i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1;
    } else {
      i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1;
    }
  } else {
    if (y0 < z0) {
      i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1;
    } else if (x0 < z0) {
      i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1;
    } else {
      i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
    }
  }

  const x1 = x0 - i1 + G3;
  const y1 = y0 - j1 + G3;
  const z1 = z0 - k1 + G3;
  const x2 = x0 - i2 + 2 * G3;
  const y2 = y0 - j2 + 2 * G3;
  const z2 = z0 - k2 + 2 * G3;
  const x3 = x0 - 1 + 3 * G3;
  const y3 = y0 - 1 + 3 * G3;
  const z3 = z0 - 1 + 3 * G3;

  let n = 0;

  let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
  if (t0 > 0) {
    t0 *= t0;
    n += t0 * t0 * gradDot(hash3i(i, j, k, seed), x0, y0, z0);
  }
  let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
  if (t1 > 0) {
    t1 *= t1;
    n += t1 * t1 * gradDot(hash3i(i + i1, j + j1, k + k1, seed), x1, y1, z1);
  }
  let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
  if (t2 > 0) {
    t2 *= t2;
    n += t2 * t2 * gradDot(hash3i(i + i2, j + j2, k + k2, seed), x2, y2, z2);
  }
  let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
  if (t3 > 0) {
    t3 *= t3;
    n += t3 * t3 * gradDot(hash3i(i + 1, j + 1, k + 1, seed), x3, y3, z3);
  }

  return 32 * n;
}

export interface FbmOptions {
  octaves?: number;
  frequency?: number;
  amplitude?: number;
  lacunarity?: number;
  gain?: number;
  seed?: number;
}

/** Fractal Brownian motion; output is normalised to roughly [-1, 1]. */
export function fbm3(x: number, y: number, z: number, opt: FbmOptions = {}): number {
  const octaves = opt.octaves ?? 5;
  const lacunarity = opt.lacunarity ?? 2.0;
  const gain = opt.gain ?? 0.5;
  const seed = opt.seed ?? 0;
  let freq = opt.frequency ?? 1;
  let amp = opt.amplitude ?? 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * simplex3(x * freq, y * freq, z * freq, seed + o * 1013);
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * Ridged multifractal. Produces sharp crests rather than rolling hills, which
 * is what makes mountain ranges read as mountain ranges. Output in [0, 1].
 */
export function ridged3(x: number, y: number, z: number, opt: FbmOptions = {}): number {
  const octaves = opt.octaves ?? 5;
  const lacunarity = opt.lacunarity ?? 2.0;
  const gain = opt.gain ?? 0.5;
  const seed = opt.seed ?? 0;
  let freq = opt.frequency ?? 1;
  let amp = opt.amplitude ?? 1;
  let sum = 0;
  let norm = 0;
  let weight = 1;
  for (let o = 0; o < octaves; o++) {
    let n = 1 - Math.abs(simplex3(x * freq, y * freq, z * freq, seed + o * 2749));
    n *= n;
    n *= weight;
    // Higher octaves are suppressed in flat regions, which keeps valleys smooth
    // and concentrates detail on the ridges themselves.
    weight = Math.min(1, Math.max(0, n * 2));
    sum += n * amp;
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

/** Billowy noise (absolute-value fbm). Output in [0, 1]. Good for dunes. */
export function billow3(x: number, y: number, z: number, opt: FbmOptions = {}): number {
  const octaves = opt.octaves ?? 4;
  const lacunarity = opt.lacunarity ?? 2.0;
  const gain = opt.gain ?? 0.5;
  const seed = opt.seed ?? 0;
  let freq = opt.frequency ?? 1;
  let amp = opt.amplitude ?? 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * Math.abs(simplex3(x * freq, y * freq, z * freq, seed + o * 4451));
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

export interface WarpResult {
  x: number;
  y: number;
  z: number;
}

/**
 * Domain warping: offsets the sample point by another noise field before
 * evaluating. This is the cheapest way to turn obviously-isotropic fractal
 * noise into something that reads as flowing, eroded geography.
 */
export function domainWarp3(
  x: number,
  y: number,
  z: number,
  strength: number,
  frequency: number,
  seed: number,
  out: WarpResult = { x: 0, y: 0, z: 0 },
): WarpResult {
  const wx = fbm3(x, y, z, { frequency, octaves: 3, seed: seed + 17 });
  const wy = fbm3(x, y, z, { frequency, octaves: 3, seed: seed + 131 });
  const wz = fbm3(x, y, z, { frequency, octaves: 3, seed: seed + 977 });
  out.x = x + wx * strength;
  out.y = y + wy * strength;
  out.z = z + wz * strength;
  return out;
}

export interface WorleyResult {
  /** Distance to the nearest feature point. */
  f1: number;
  /** Distance to the second nearest feature point. */
  f2: number;
  /** Stable hash identifying the nearest cell; use it for per-cell properties. */
  cell: number;
  /** Nearest feature point position. */
  px: number;
  py: number;
  pz: number;
}

const worleyScratch: WorleyResult = { f1: 0, f2: 0, cell: 0, px: 0, py: 0, pz: 0 };

/**
 * 3D Worley / cellular noise with jittered feature points.
 *
 * Used for tectonic plates (F2 - F1 gives plate boundaries), crystal fields and
 * crater fields. Searching the full 3x3x3 neighbourhood keeps it correct for
 * any jitter up to 1.0.
 */
export function worley3(
  x: number,
  y: number,
  z: number,
  seed = 0,
  jitter = 1.0,
  out: WorleyResult = worleyScratch,
): WorleyResult {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  let f1 = Infinity;
  let f2 = Infinity;
  let cell = 0;
  let px = 0;
  let py = 0;
  let pz = 0;

  for (let dz = -1; dz <= 1; dz++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const cx = xi + dx;
        const cy = yi + dy;
        const cz = zi + dz;
        const h = hash3i(cx, cy, cz, seed);
        const ox = toUnit(h) * jitter;
        const oy = toUnit(h ^ 0x9e3779b9) * jitter;
        const oz = toUnit(h ^ 0x85ebca6b) * jitter;
        const fx = cx + ox;
        const fy = cy + oy;
        const fz = cz + oz;
        const ddx = fx - x;
        const ddy = fy - y;
        const ddz = fz - z;
        const d = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz);
        if (d < f1) {
          f2 = f1;
          f1 = d;
          cell = h;
          px = fx;
          py = fy;
          pz = fz;
        } else if (d < f2) {
          f2 = d;
        }
      }
    }
  }

  out.f1 = f1;
  out.f2 = f2;
  out.cell = cell;
  out.px = px;
  out.py = py;
  out.pz = pz;
  return out;
}

/**
 * Sums the analytic gradient of an fbm field by central differences.
 * `eps` should be small relative to the highest frequency in use.
 */
export function fbmGradient(
  x: number,
  y: number,
  z: number,
  eps: number,
  opt: FbmOptions,
  out: WarpResult = { x: 0, y: 0, z: 0 },
): WarpResult {
  const inv = 1 / (2 * eps);
  out.x = (fbm3(x + eps, y, z, opt) - fbm3(x - eps, y, z, opt)) * inv;
  out.y = (fbm3(x, y + eps, z, opt) - fbm3(x, y - eps, z, opt)) * inv;
  out.z = (fbm3(x, y, z + eps, opt) - fbm3(x, y, z - eps, opt)) * inv;
  return out;
}
